import type { Context, Metrics, ReportResult, ReportRow } from './types.js';
import { d, n } from './math.js';
export type DisplayMode = 'portfolio' | 'subfolio' | 'shares';
export type MatrixValue = number | string | null;
export type MatrixValues = ReturnType<typeof matrixValues>;
const sum = (a: number | null, b: number) => a === null ? null : n(d(a).add(b));
const ratio = (a: number | null, b: number | null) => a === null || b === null ? null : b > 0 ? n(d(a).div(b).mul(100)) : 0;
export const matrixName = (row: ReportRow) => row.symbol ? `${row.symbol} - ${row.label}` : row.description ? `${row.label} (${row.description})` : row.label;

/** Rendered reports need the exact decimal sum, rather than every dated transfer. */
export function matrixTransferTotal(metrics: Metrics): string {
  return metrics.boundaryTransferTotal ?? metrics.cashflows.filter(flow => flow.kind === 'transfer')
    .reduce((total, flow) => total.add(flow.amount), d(0)).toString();
}

/** Cost-based metrics use historical allocated cost. Period-performance metrics keep
 * their existing capital denominator; older snapshots retain their original presentation.
 */
export function matrixValues(row: ReportRow, context: Context, convention: 'cash' | 'ex-date' = 'ex-date') {
  const m = row.contexts[context];
  const single = row.level === 'security' || row.level === 'combined-security';
  const startAndBuys = sum(m.openingValue, m.purchaseCost);
  // Transfers crossing the reporting boundary adjust period capital; internal pairs net out.
  const transfers = d(matrixTransferTotal(m));
  const periodCost = startAndBuys === null ? null : n(d(startAndBuys).sub(transfers));
  const performanceCapital = context === 'realised' ? m.saleCost : periodCost;
  const cost = m.historicalCostBasis === undefined ? performanceCapital : m.historicalCostBasis;
  const capital = m.capitalProfit === null ? null : context === 'realised' ? n(d(m.saleProceeds).sub(m.saleCost)) : m.capitalProfit;
  const total = sum(capital, m.dividends);
  const denominator = context === 'realised' ? m.soldUnits : context === 'overall' ? m.soldUnits + m.units : m.units || m.openingUnits;
  const quote = (key: 'opening' | 'closing'): number | null => {
    if (!single) return null;
    if (row.prices?.[key]?.price != null) return row.prices[key]!.price;
    return row.children.find(child => child.prices?.[key]?.price != null)?.prices?.[key]?.price ?? null;
  };
  return {
    sharesAtStart: single ? m.openingUnits : null, sharesAtEnd: single ? m.units : null,
    sharesSoldInPeriod: single ? m.soldUnits : null,
    sharePriceAtStart: quote('opening'), sharePriceAtEnd: quote('closing'),
    avgCostPerShare: !single || cost === null ? null : denominator > 0 && cost > 0 ? n(d(cost).div(denominator)) : 0,
    valueAtStartPlusCostOfNewBuys: startAndBuys, costBasisAtEnd: cost,
    costOfSharesSold: m.saleCost, totalSalesPrice: m.saleProceeds,
    valueAtEnd: m.closingValue, valueOfHoldings: m.closingValue, saleProceeds: m.saleProceeds,
    totalPeriodValue: sum(m.closingValue, m.saleProceeds), capitalGainLoss: capital,
    capitalGainLossPercent: ratio(capital, performanceCapital),
    dividendsPaid: convention === 'cash' ? m.dividends : m.dividendsPaid,
    dividendsOutstanding: m.dividendsOutstanding, totalDividends: m.dividends,
    dividendReturnOnCost: ratio(m.dividends, cost), dividendYieldOnStart: ratio(m.dividends, startAndBuys),
    totalProfitLoss: total, totalProfitLossPercent: ratio(total, performanceCapital),
    lastBuyDate: row.lastBuyDate ?? null, xirrPercent: m.irr.rate === null ? null : m.irr.rate * 100,
  };
}
export function matrixRoots(report: ReportResult, context: Context, display: DisplayMode): ReportRow[] {
  const include = (row: ReportRow) => !['security', 'combined-security'].includes(row.level) || context === 'overall' || (context === 'unrealised' ? row.contexts.unrealised.units > 0 : row.contexts.realised.soldUnits > 0);
  const filter = (rows: ReportRow[]): ReportRow[] => rows.filter(include).map(row => ({ ...row, prices: row.prices ?? (row.level === 'combined-security' ? { opening: row.children.find(c => c.prices?.opening?.price != null)?.prices?.opening ?? null, closing: row.children.find(c => c.prices?.closing?.price != null)?.prices?.closing ?? null } : undefined), children: row.level === 'combined-security' ? [] : filter(row.children) }))
    .sort((a, b) => matrixName(a).localeCompare(matrixName(b), 'en', { numeric: true }));
  return filter(display === 'shares' ? report.combinedSecurities : display === 'subfolio' ? report.portfolios.flatMap(p => p.children) : report.portfolios);
}
export function expandedMatrixRows(report: ReportResult, context: Context, display: DisplayMode, expandSubfolios = true, expandShares = true): ReportRow[] {
  const visit = (rows: ReportRow[]): ReportRow[] => rows.flatMap(row => [row, ...((row.level === 'portfolio' ? expandSubfolios : expandShares) ? visit(row.children) : [])]);
  return [report.total, ...visit(matrixRoots(report, context, display))];
}
